// External
import { FormArray, FormGroup } from '@angular/forms';
import { EventEmitter } from '@angular/core';

// Internal
import { UiButton } from '../ui-button/ui-button.models';

export enum FormControlStatus {
    VALID = 'VALID',
    INVALID = 'INVALID',
    PENDING = 'PENDING',
    DISABLED = 'DISABLED'
}

export namespace UiForm {
    export import Size = UiButton.Size;

    export enum Orientation {
        /**
         * left-to-right
         */
        LTR = 'ltr',
        /**
         * right-to-left
         */
        RTL = 'rtl'
    };

    export enum Alignment {
        START  = 'start',
        CENTER = 'center',
        END    = 'end'
    };
    export namespace Input {
        /**
         *
         * Base properties for input-control component
         * @export
         * @interface Component
         */
        export interface Component {
            /**
             * Parent form for the input control
             * @type {(FormGroup | FormArray)}
             * @memberof Base
             */
            form?: FormGroup | FormArray;

            /**
             * Control name that is linked to the input
             * @type {string}
             * @memberof Base
             */
            controlName?: string;

            /**
             * State of the input
             * @type {boolean}
             * @memberof Base
             */
            isValid?: boolean;

            /**
             * If input is optional
             * @type {boolean}
             * @memberof Base
             */
            optional?: boolean;

            /**
             * Show Character count.
             * Will take tha maxLength from formControl
             * @type {boolean}
             * @memberof Base
             */
            characterCount?: boolean;

            /**
             * Max length of the input value
             * @type {number}
             * @memberof Component
             */
            maxLength?: number;
            /**
             * Linked with Buttons sizes
             * @type {Size}
             * @memberof Base
             */
            size?: Size;

            /**
             * Add prefix or suffix
             * @type {(Addons | Addons[])}
             * @memberof Base
             */
            addons?: Addons | Addons[];

            /**
             * If one addone is focus, automaticaly focus the input
             * @type {boolean}
             * @memberof Base
             */
            addonsFocus ?: boolean;

            /**
             * @type {object | string}
             * @memberof Base
             */
            hint?: object | string;

            /**
             * OPTIONAL. If hint (or error) text dosen't need translate
             * @type {boolean}
             * @memberof Base
             */
            staticText?: boolean;

            /**
             * Show hint
             * @type {boolean}
             * @memberof Base
             */
            showHint?: boolean;

            /**
             * Put the input in a host with 'pill' class
             * @type {boolean}
             * @memberof Base
             */
            pill ?: boolean;

            /**
             * Add 'boxed' class to the input
             * @type {boolean}
             * @memberof Base
             */
            boxed ?: boolean;
        }

        export import Size = UiForm.Size;

        export enum Addons {
            PREFIX = 'prefix',
            SUFFIX = 'suffix'
        }
    }

    export namespace Select {
        /**
         *
         * Base properties for Select-control component
         * @export
         * @interface Component
         */
        export interface Component {
            /**
             *
             * Id for select-control
             * @type {string}
             * @memberof Component
             */
            selectId: string;

            /**
             *
             * Initial selected option
             * @type {number}
             * @memberof Component
             */
            selectedOption: number

           /**
            * - controls select's disabled state, not each option individually
            * - for disabling specific option use [disabled]="var:boolean" on that option
            * @type {boolean}
            * @memberof Component
            */
            disabled:boolean;

            /**
             *
             * @type {string}
             * @memberof Component
             */
            hint: string;

            /**
             * OPTIONAL. If hint (or error) text dosen't need translate
             * @type {boolean}
             * @memberof Base
             */
            staticText?: boolean;

            /**
             *
             * Show hint
             * @type {boolean}
             * @memberof Component
             */
            showHint: boolean;

            /**
             *
             * State of the input
             * @type {boolean}
             * @memberof Component
             */
            isValid: boolean;

            // layout stuff
            /**
             *
             * Box layout
             * @type {boolean}
             * @memberof Component
             */
            boxed: boolean

            /**
             *
             * Direction to open
             * @type {Direction}
             * @memberof Component
             */
            direction: Direction;

            /**
             *
             * Default geight
             * @type {number}
             * @memberof Component
             */
            limitScroll: number; // default height

            /**
             *
             * Linked with Buttons sizes
             * @type {Size}
             * @memberof Component
             */
            size: Size;

            /**
             *
             *
             * @type {EventEmitter<number>}
             * @memberof Component
             */
            selectedOptionChange: EventEmitter<number>;
            /**
             *
             *
             * @type {EventEmitter<any>}
             * @memberof Component
             */
            selectedOptionValue: EventEmitter<any>;
            /**
             *
             *
             * @type {EventEmitter<any>}
             * @memberof Component
             */
            onClose: EventEmitter<any>;
        }

        export import Size = UiForm.Size;

        export enum Direction {
            UP   = 'up',
            DOWN = 'down'
        }
    }

    export namespace Radio {
        export interface Component {
            /**
             *
             * @type {Alignment}
             * @memberof Component
             */
            alignment: Alignment
        }
    }

    export namespace Checkbox {
        export interface Component {
            /**
             * @type {boolean}
             * @memberof Component
             */
            isValid: boolean;

            /**
             * @type {string}
             * @memberof Component
             */
            hint: string;

            /**
             * @type {boolean}
             * @memberof Component
             */
            showHint: boolean;
        }
    }

    export namespace RangeSlider {
        export interface Component {
            /**
             * Ex: primary, danger, success
             * @type {string}
             * @memberof Component
             */
            color: string;
            /**
             * @type {string}
             * @memberof Component
             */
            valueLabel: string;

            /**
             * @type {string}
             * @memberof Component
             */
            id: string;

            /**
             * @type {boolean}
             * @memberof Component
             */
            disabled: boolean;

            // value inputs, max is required
            /**
             *
             *
             * @type {number}
             * @memberof Component
             */
            value: number;
            /**
             *
             *
             * @type {number}
             * @memberof Component
             */
            min: number;
            /**
             *
             *
             * @type {number}
             * @memberof Component
             */
            max: number;

            /**
             *
             *
             * @type {EventEmitter<number>}
             * @memberof Component
             */
            valueChange: EventEmitter<number>;
        }
    }

    export namespace SwitchControl {
        export interface Component {
            /**
             *
             * Optional LTR/RTL
             * @type {Orientation}
             * @memberof Component
             */
            orientation?: Orientation;

            /**
             *
             *  Optional START/CENTER/END
             * @type {Alignment}
             * @memberof Component
             */
            alignment?: Alignment;

            /**
             *
             * OPTIONAL SUFFIX/PREFIX
             * @type {Addons}
             * @memberof Component
             */
            addons?: Addons
        }

        export import Addons = Input.Addons;
    }
}